import React, { useCallback, useMemo, useState } from 'react';
import {
  createZoomedCropBlob,
  createShareCardBlob,
  shareOrDownloadImageBlob,
} from '../../../utils/zoomedImageShare';
import { buildShareText, buildStatusRows } from '../../../utils/wordleShare';
import './SharePanel.css';

// Image size for clipboard (plain image copy / share).
const COPY_IMAGE_SIZE = 512;

// Width for the share card (photo + results baked in).
// 360px keeps it fast while still readable.
const CARD_WIDTH = 360;

export default function ShareZoomedImageButton({
  imageUrl,
  solution,
  zoomLevel,
  zoomOrigin = null,
  gameName = 'Game',
  guesses = [],
  maxGuesses = 5,
  disabled = false,
}) {
  const [busy, setBusy] = useState(false);
  const [copiedCard, setCopiedCard] = useState(false);   // baked-in card
  const [copiedCombo, setCopiedCombo] = useState(false);  // old-style image + text
  const [copiedText, setCopiedText] = useState(false);
  const [error, setError] = useState('');

  const canCopyToClipboard = useMemo(() => {
    return Boolean(
      window?.isSecureContext &&
        navigator?.clipboard?.write &&
        typeof window.ClipboardItem !== 'undefined'
    );
  }, []);

  const canCopyText = useMemo(() => {
    return Boolean(navigator?.clipboard?.writeText);
  }, []);

  const fileName = useMemo(() => {
    const safeWord = String(solution || 'word')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .slice(0, 32);
    return `zoomed-${safeWord}.png`;
  }, [solution]);

  const shareText = useMemo(
    () => buildShareText({ gameName, guesses, solution, maxGuesses }),
    [gameName, guesses, solution, maxGuesses]
  );

  const statusRows = useMemo(
    () => buildStatusRows({ guesses, solution }),
    [guesses, solution]
  );

  const createBlob = useCallback(async () => {
    return await createZoomedCropBlob({
      imageUrl,
      zoomLevel,
      zoomOrigin,
      outputSize: COPY_IMAGE_SIZE,
      type: 'image/png',
    });
  }, [imageUrl, zoomLevel, zoomOrigin]);

  // Helper to reset feedback states
  const resetFeedback = useCallback(() => {
    setCopiedCard(false);
    setCopiedCombo(false);
    setCopiedText(false);
    setError('');
  }, []);

  // ---- 1) Baked-in card: photo + results rendered into one image ----
  const handleCopyCard = useCallback(async () => {
    if (!imageUrl || busy) return;

    setBusy(true);
    resetFeedback();

    try {
      if (!canCopyToClipboard) {
        throw new Error(
          window?.isSecureContext
            ? 'Copy image is not supported in this browser.'
            : 'Copy image needs https (or localhost). Use download instead.'
        );
      }

      const cardBlob = await createShareCardBlob({
        imageUrl,
        zoomLevel,
        zoomOrigin,
        gameName,
        solution,
        statusRows,
        maxGuesses,
        cardWidth: CARD_WIDTH,
      });

      // Short CTA text for text-only apps (Notepad).
      const ctaText = 'Try Now For Free at zoomed-in.com';
      const textBlob = new Blob([ctaText], { type: 'text/plain' });
      const item = new window.ClipboardItem({
        'image/png': cardBlob,
        'text/plain': textBlob,
      });
      await navigator.clipboard.write([item]);

      setCopiedCard(true);
      setTimeout(() => setCopiedCard(false), 1500);
    } catch (e) {
      handleCopyError(e);
    } finally {
      setBusy(false);
    }
  }, [busy, canCopyToClipboard, imageUrl, zoomLevel, zoomOrigin, gameName, solution, statusRows, maxGuesses, shareText, resetFeedback]);

  // ---- 2) Old-style: plain zoomed image + text as separate MIME types ----
  const handleCopyCombo = useCallback(async () => {
    if (!imageUrl || busy) return;

    setBusy(true);
    resetFeedback();

    try {
      if (!canCopyToClipboard) {
        throw new Error(
          window?.isSecureContext
            ? 'Copy image is not supported in this browser.'
            : 'Copy image needs https (or localhost). Use download instead.'
        );
      }

      const imageBlob = await createBlob();
      const textBlob = new Blob([shareText || ''], { type: 'text/plain' });

      const item = new window.ClipboardItem({
        'image/png': imageBlob,
        'text/plain': textBlob,
      });
      await navigator.clipboard.write([item]);

      setCopiedCombo(true);
      setTimeout(() => setCopiedCombo(false), 1500);
    } catch (e) {
      handleCopyError(e);
    } finally {
      setBusy(false);
    }
  }, [busy, canCopyToClipboard, createBlob, imageUrl, shareText, resetFeedback]);

  // Shared error handler for image copy failures
  const handleCopyError = useCallback(async (e) => {
    let message =
      e?.name === 'SecurityError'
        ? 'Image host blocks exporting. Copied results instead.'
        : e?.message || 'Failed to copy.';

    if (shareText && navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareText);
        setCopiedText(true);
        setTimeout(() => setCopiedText(false), 1500);
        if (e?.name !== 'SecurityError') {
          message = `${message} Copied results instead.`;
        }
      } catch {
        // keep original message
      }
    }

    setError(message);
  }, [shareText]);

  // ---- Copy text results only ----
  const handleCopyText = useCallback(async () => {
    if (!shareText || busy) return;

    setBusy(true);
    setCopiedText(false);
    setError('');

    try {
      if (!canCopyText) {
        throw new Error('Clipboard not supported in this browser.');
      }
      await navigator.clipboard.writeText(shareText);
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 1500);
    } catch (e) {
      setError(e?.message || 'Failed to copy results.');
    } finally {
      setBusy(false);
    }
  }, [busy, canCopyText, shareText]);

  // ---- Share / download image file ----
  const handleShareOrDownload = useCallback(async () => {
    if (!imageUrl || busy) return;

    setBusy(true);
    setError('');

    try {
      const blob = await createBlob();

      await shareOrDownloadImageBlob({
        blob,
        fileName,
        shareTitle: gameName,
        shareText: shareText || '',
      });
    } catch (e) {
      const message =
        e?.name === 'SecurityError'
          ? 'Image host blocks exporting.'
          : e?.message || 'Failed to share image.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }, [busy, createBlob, fileName, gameName, imageUrl, shareText]);

  const allDisabled = disabled || busy;

  return (
    <div className="share-panel">
      {/* Row 1: text results + plain image+text */}
      <div className="share-panel__row">
        <button
          className={`share-panel__btn ${copiedText ? 'share-panel__btn--success' : ''}`}
          onClick={handleCopyText}
          disabled={allDisabled || !shareText || !canCopyText}
        >
          {busy ? 'Copying…' : copiedText ? 'Copied!' : 'Copy results'}
        </button>

        <button
          className={`share-panel__btn ${copiedCombo ? 'share-panel__btn--success' : ''}`}
          onClick={handleCopyCombo}
          disabled={allDisabled || !imageUrl || !canCopyToClipboard}
        >
          {busy ? 'Preparing…' : copiedCombo ? 'Copied!' : 'Copy image + results'}
        </button>
      </div>

      {/* Row 2: baked-in card */}
      <div className="share-panel__row">
        <button
          className={`share-panel__btn ${copiedCard ? 'share-panel__btn--success' : ''}`}
          onClick={handleCopyCard}
          disabled={allDisabled || !imageUrl || !canCopyToClipboard}
        >
          {busy ? 'Preparing…' : copiedCard ? 'Copied!' : 'Copy Image Card (results baked in image)'}
        </button>
      </div>

      {/* Small link-style secondary action */}
      <button
        className="share-panel__link"
        onClick={handleShareOrDownload}
        disabled={allDisabled || !imageUrl}
      >
        {busy ? 'Preparing…' : 'Share / download image'}
      </button>

      {error && <p className="share-panel__error">{error}</p>}
    </div>
  );
}
